import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Clock, Briefcase, Send, ChevronRight, Building2, Users } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ScrollReveal from '../components/ScrollReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API}/job-openings`);
        setJobs(res.data);
      } catch {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const openJobs = jobs.filter(j => j.status === 'open');
  const departments = [...new Set(openJobs.map(j => j.department))];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Careers — Join Our Team"
        description="Join SDORA Wellbeing LLC. Explore open positions in mental health therapy, psychiatric rehabilitation, case management, and administration in Pikesville & Baltimore County, MD. Make a difference in behavioral health."
        path="/careers"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 text-blue-200 mb-4">
                <Building2 className="w-5 h-5" />
                <span className="text-sm font-semibold uppercase tracking-wide">Careers at SDORA Wellbeing</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Make a Difference in Mental Health
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed">
                Join a compassionate team dedicated to transforming lives through evidence-based behavioral health care in Baltimore County and beyond.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">Pikesville, MD</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">{openJobs.length} Open Position{openJobs.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Why Work With Us */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">Why Join SDORA Wellbeing?</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Meaningful Work', desc: 'Directly impact the lives of individuals and families navigating mental health challenges in our community.' },
              { title: 'Professional Growth', desc: 'Access ongoing training, clinical supervision, and career advancement opportunities within a CARF-accredited organization.' },
              { title: 'Supportive Culture', desc: 'Work alongside a diverse, compassionate team that values cultural competency, collaboration, and work-life balance.' }
            ].map((item, i) => (
              <ScrollReveal key={item.title} direction="up" delay={i * 0.1}>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 h-full">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div className="py-16" data-testid="job-listings">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Open Positions</h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : openJobs.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Open Positions Right Now</h3>
              <p className="text-gray-500 mb-6">Check back soon or send us your resume for future opportunities.</p>
              <a
                href="mailto:info@sdorawellbeing.org?subject=Career%20Inquiry"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                <Send className="w-4 h-4" />
                Send Your Resume
              </a>
            </div>
          ) : (
            <div className="space-y-10">
              {departments.map((dept) => (
                <div key={dept}>
                  <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-4">{dept}</h3>
                  <div className="space-y-3">
                    {openJobs.filter(j => j.department === dept).map((job) => (
                      <div
                        key={job.id}
                        className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                        data-testid={`job-card-${job.id}`}
                      >
                        <button
                          onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                          className="w-full text-left px-6 py-4 flex items-center justify-between"
                        >
                          <div>
                            <h4 className="text-lg font-bold text-gray-900">{job.title}</h4>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{job.type}</span>
                              <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.department}</span>
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedJob === job.id ? 'rotate-90' : ''}`} />
                        </button>

                        {expandedJob === job.id && (
                          <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                            <p className="text-gray-700 text-sm leading-relaxed mb-4">{job.description}</p>

                            {job.responsibilities && job.responsibilities.length > 0 && (
                              <div className="mb-4">
                                <h5 className="text-sm font-bold text-gray-900 mb-2">Responsibilities:</h5>
                                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                  {job.responsibilities.map((r) => <li key={r}>{r}</li>)}
                                </ul>
                              </div>
                            )}

                            {job.qualifications && job.qualifications.length > 0 && (
                              <div className="mb-4">
                                <h5 className="text-sm font-bold text-gray-900 mb-2">Qualifications:</h5>
                                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                  {job.qualifications.map((q) => <li key={q}>{q}</li>)}
                                </ul>
                              </div>
                            )}

                            <a
                              href={`mailto:info@sdorawellbeing.org?subject=Application%20-%20${encodeURIComponent(job.title)}`}
                              className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm mt-2"
                              data-testid={`apply-btn-${job.id}`}
                            >
                              <Send className="w-4 h-4" />
                              Apply Now
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* General Application CTA */}
      <div className="py-16 bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Don't See a Fit? We'd Still Love to Hear From You</h2>
          <p className="text-lg text-green-100 mb-8 max-w-2xl mx-auto">
            Send your resume and a brief introduction to our team. We're always looking for passionate professionals.
          </p>
          <a
            href="mailto:info@sdorawellbeing.org?subject=General%20Career%20Inquiry"
            className="inline-flex items-center gap-2 bg-white text-green-700 px-8 py-3 rounded-lg hover:bg-green-50 transition-colors font-bold text-lg"
            data-testid="general-apply-btn"
          >
            <Send className="w-5 h-5" />
            Submit Your Resume
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CareersPage;
